import { Description, Field, Label } from '../catalyst/fieldset'
import { Input } from '../catalyst/input'
import { Select } from '../catalyst/select'
import { amountToCanonical, unitLong, unitsOf, unitShort } from './stock-units'
import type { StockAmount, StockDimension, StockItem } from './types'

// One amount of stock as a person types it: whole packages (when the item has a package) and/or a
// loose amount in any unit of the item's dimension. The server takes the same shape (StockAmount).

export type AmountDraft = { packages: string; value: string; unit: string }

export const emptyAmount = (unit: string): AmountDraft => ({ packages: '', value: '', unit })

const num = (s: string) => (s.trim() === '' ? null : Number(s))

/** The amount to send, or null while nothing (or something unreadable) is typed. */
export function parseAmount(draft: AmountDraft, hasPackage: boolean): StockAmount | null {
  const packages = hasPackage ? num(draft.packages) : null
  const value = num(draft.value)
  if ((packages === null && value === null) || Number.isNaN(packages) || Number.isNaN(value)) return null
  return { ...(packages !== null ? { packages } : {}), ...(value !== null ? { value, unit: draft.unit } : {}) }
}

/** The canonical amount of a draft, or null. */
export function draftCanonical(item: Pick<StockItem, 'dimension' | 'package'>, draft: AmountDraft): number | null {
  const amount = parseAmount(draft, !!item.package)
  if (!amount) return null
  return amountToCanonical(item, { packages: amount.packages ?? null, value: amount.value ?? null, unit: amount.unit ?? draft.unit })
}

function UnitSelect({ dimension, value, onChange }: { dimension: StockDimension; value: string; onChange: (unit: string) => void }) {
  return (
    <Select aria-label="Unit" value={value} onChange={(e) => onChange(e.target.value)}>
      {unitsOf(dimension).map((u) => (
        <option key={u} value={u}>
          {unitShort(dimension, u) || unitLong(dimension, u)}
        </option>
      ))}
    </Select>
  )
}

export function StockAmountInput({
  label,
  description,
  dimension,
  pkg,
  draft,
  onChange,
  invalid,
  required,
  autoFocus,
}: {
  label: string
  description?: React.ReactNode
  dimension: StockDimension
  pkg: { name: string; size: number; unit: string } | null
  draft: AmountDraft
  onChange: (draft: AmountDraft) => void
  invalid?: boolean
  required?: boolean
  autoFocus?: boolean
}) {
  const set = (patch: Partial<AmountDraft>) => onChange({ ...draft, ...patch })
  const count = dimension === 'COUNT'
  const loose = (
    <div className="flex gap-2">
      <Input
        aria-label={pkg ? `Plus, in ${unitShort(dimension, draft.unit) || 'units'}` : label}
        type="number"
        inputMode="decimal"
        min="0"
        step="any"
        value={draft.value}
        onChange={(e) => set({ value: e.target.value })}
        invalid={invalid}
        required={required && !pkg}
        autoFocus={autoFocus && !pkg}
      />
      {!count && (
        <div className="w-32 shrink-0">
          <UnitSelect dimension={dimension} value={draft.unit} onChange={(unit) => set({ unit })} />
        </div>
      )}
    </div>
  )

  return (
    <Field>
      <Label>{label}</Label>
      {description && <Description>{description}</Description>}
      {pkg ? (
        <div data-slot="control" className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <span className="text-sm/6 text-zinc-500 dark:text-zinc-400">
              Full {pkg.name.toLowerCase()}s ({pkg.size.toLocaleString()} {unitShort(dimension, pkg.unit)} each)
            </span>
            <Input
              aria-label={`Full ${pkg.name.toLowerCase()}s`}
              type="number"
              inputMode="numeric"
              min="0"
              step="any"
              value={draft.packages}
              onChange={(e) => set({ packages: e.target.value })}
              invalid={invalid}
              autoFocus={autoFocus}
            />
          </div>
          <div>
            <span className="text-sm/6 text-zinc-500 dark:text-zinc-400">Plus loose (optional)</span>
            {loose}
          </div>
        </div>
      ) : (
        <div data-slot="control">{loose}</div>
      )}
    </Field>
  )
}
